import useTranslations from 'contexts/basicData/useTranslations';
import { useCallback, useMemo } from 'react';
import useGlobalSettings from './useGlobalSettings';
import { GlobalSettingEnum, ZipCodeArea } from 'api/model';

/**
 * Länsraden postnumret hör till. Intervallen överlappar (Södermanland inuti Östergötland,
 * Blekinge inuti Skåne, Halland och Jönköping inuti Västra Götaland): det smalaste intervallet
 * vinner, lika breda avgörs på lägst id — samma regel som ZipCodeAreaHelper.FindArea i backend.
 */
export const findZipCodeAreaByNumber = (
  zipCodeAreas: ZipCodeArea[],
  zipNumber: number
): ZipCodeArea | undefined =>
  zipCodeAreas
    .filter((zca) => zca.minCode <= zipNumber && zca.maxCode >= zipNumber)
    .sort(
      (a, b) => a.maxCode - a.minCode - (b.maxCode - b.minCode) || a.id - b.id
    )[0];

const useZipCodeAreas = () => {
  const { zipCodeAreas } = useTranslations();
  const { parsedData } = useGlobalSettings();
  const largeCounties = useMemo(
    () =>
      Object.values(zipCodeAreas).filter((zca) =>
        parsedData[GlobalSettingEnum.CountyNamesCountedAsLargeCities].includes(
          zca.areaName
        )
      ),
    [parsedData, zipCodeAreas]
  );

  const allZipCodeAreas = useMemo(
    () =>
      Object.values(zipCodeAreas).sort((a, b) =>
        a.areaName > b.areaName ? 1 : -1
      ),
    [zipCodeAreas]
  );

  const zipCodeAreaByName = useMemo(() => {
    const areasByName = {} as { [areaName: string]: ZipCodeArea };
    allZipCodeAreas.forEach((zca) => {
      if (!(zca.areaName in areasByName)) {
        areasByName[zca.areaName] = zca;
      }
    });

    return areasByName;
  }, [allZipCodeAreas]);

  const selectableZipCodeAreas = useMemo(() => {
    return Object.values(zipCodeAreaByName).sort((a, b) =>
      a.areaName > b.areaName ? 1 : -1
    );
  }, [zipCodeAreaByName]);

  const zipCodeAreaFromZipCode = useCallback(
    (zipCode: string) => {
      const zipNumber = Number(zipCode.replace(' ', ''));
      if (!Number.isNaN(zipNumber) && zipNumber >= 10000) {
        const zipArea = findZipCodeAreaByNumber(allZipCodeAreas, zipNumber);
        return zipArea?.areaName !== undefined
          ? zipCodeAreaByName[zipArea.areaName]
          : undefined;
      }

      return undefined;
    },
    [allZipCodeAreas, zipCodeAreaByName]
  );

  const isOutsideAllowedRange = (zipCode1?: string, zipCode2?: string) => {
    // return false if they are undefined
    if (!zipCode1 || !zipCode2) {
      return false;
    }
    if (zipCode1 === zipCode2) {
      return false; // Same zip code, so no need to check
    }
    const matchingFirstTwoDigits =
      zipCode1.substring(0, 2) === zipCode2.substring(0, 2);

    if (matchingFirstTwoDigits) {
      return false; // Is always within the same county
    }

    if (largeCounties.length === 0) {
      return true; // No large counties that need to be checked
    }

    const zip1 = parseInt(zipCode1.replace(' ', ''), 10);
    const zip2 = parseInt(zipCode2.replace(' ', ''), 10);

    // Länet avgörs mot alla län och inte bara mot storstadslänen: Jönköping och Halland ligger
    // inuti Västra Götalands intervall och ska inte räknas dit.
    const largeCountyOf = (zipNumber: number) => {
      const area = findZipCodeAreaByNumber(allZipCodeAreas, zipNumber);
      return area && largeCounties.some((c) => c.areaName === area.areaName)
        ? area
        : undefined;
    };
    const county1 = largeCountyOf(zip1);
    const county2 = largeCountyOf(zip2);

    if (!county1 || !county2) {
      return true; // One of the zips are not within a large county, so we assume they are different
    }

    if (county1.areaName === county2.areaName) {
      return false; // Both zips are within the same large county, which is allowed
    }

    return true; // The zips are within different large counties
  };

  const zipCodeAreaNames = Object.keys(zipCodeAreas);

  return {
    allZipCodeAreas,
    zipCodeAreaByName,
    selectableZipCodeAreas,
    zipCodeAreaNames,
    zipCodeAreaFromZipCode,
    isOutsideAllowedRange,
  };
};

export default useZipCodeAreas;
